import { useState, useRef, useEffect } from 'react';
import axios from 'axios';

export const useChat = () => {
  const [chatMessages, setChatMessages] = useState([]);
  const [userMessage, setUserMessage] = useState('');
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [history, setHistory] = useState(null);
  const [moduleId, setModuleId] = useState(null);
  const [submoduleNumber, setSubmoduleNumber] = useState(null);
  const [textPassage, setTextPassage] = useState('');
  const [moduleSubmoduleContextDict, setModuleSubmoduleContextDict] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [firstAssistantMessage, setFirstAssistantMessage] = useState('');
  const [lastAssistantMessage, setLastAssistantMessage] = useState('');
  
  // Timer states
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Performance summary states
  const [showCompleteOverlay, setShowCompleteOverlay] = useState(false);
  const [performanceSummary, setPerformanceSummary] = useState('');
  const [isGeneratingPerformanceSummary, setIsGeneratingPerformanceSummary] = useState(false);
  
  // First message modal
  const [showFirstMessageModal, setShowFirstMessageModal] = useState(false);
  const [hasEnoughMessages, setHasEnoughMessages] = useState(false);
  
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    setHasEnoughMessages(chatMessages.length >= 5);
  }, [chatMessages]);

  // Timer effect
  useEffect(() => {
    let timerInterval;
    if (startTime) {
      timerInterval = setInterval(() => {
        const now = Date.now();
        setElapsedTime(Math.floor((now - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(timerInterval);
  }, [startTime]);

  const initModuleChat = async (userId, sessionId, moduleId, chosenSubtopic) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/init_module_chat`,
        {
          user_id: userId,
          session_id: sessionId,
          module_id: moduleId,
          chosen_passage_subtopic: chosenSubtopic
        },
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );

      setChatMessages([{ sender: 'system', message: response.data.first_message }]);
      setFirstAssistantMessage(response.data.first_message);
      setSessionId(response.data.session_id);
      setHistory(response.data.history);
      setModuleId(response.data.module_id);
      setUserMessage('');
      setSubmoduleNumber(response.data.submodule_number);
      setTextPassage(response.data.text_passage);
      setModuleSubmoduleContextDict(response.data.module_submodule_context_dict);

      return { success: true, data: response.data };
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'CanceledError' || error.name === 'AbortError') {
        console.log('Request cancelled');
      } else {
        console.error('Error initializing chat:', error);
      }
      return { success: false };
    }
  };

  const sendMessage = async (userId, selectedModule) => {
    if (!userMessage) return;

    setIsSending(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/chat`, {
        user_id: userId,
        session_id: sessionId,
        history: history,
        message: userMessage,
        module_id: selectedModule,
        submodule_number: submoduleNumber,
        text_passage: textPassage,
        module_submodule_context_dict: moduleSubmoduleContextDict
      });

      const systemMsg = { sender: 'system', message: response.data.response };
      const updatedChat = [
        ...chatMessages,
        { sender: 'user', message: userMessage },
        systemMsg
      ];

      console.log('Chat Response =', response.data.response)

      setUserMessage('');
      setHistory(response.data.history);
      setSubmoduleNumber(response.data.submodule_number);
      setTextPassage(response.data.text_passage);
      setModuleSubmoduleContextDict(response.data.module_submodule_context_dict);
      // increment the user message count by 1
      setUserMessageCount(prev => prev + 1);

      if (response.data.exit_message) {
        // No performance summary yet, show generating spinner/message
        setIsGeneratingPerformanceSummary(true);
        setShowFirstMessageModal(false);

        // Now fetch performance summary and input completed passage info
        const summaryResponse = await axios.post(`${process.env.REACT_APP_API_URL}/api/performance_summary`, {
          session_id: sessionId,
          user_id: userId,
          module_id: selectedModule
        });

        setLastAssistantMessage(response.data.response)
        setPerformanceSummary(summaryResponse.data.performance_summary);
        setIsGeneratingPerformanceSummary(false);
        setShowCompleteOverlay(true);

        return { exitMessage: true, data: summaryResponse.data, feedback: response.data.response};
      } else {
        setChatMessages(updatedChat);
      }

      return { exitMessage: false };
    } catch (error) {
      console.error('Error sending message:', error);
      return { error };
    } finally {
      setIsSending(false);
    }
  };

  const resetChat = () => {
    setChatMessages([]);
    setUserMessage('');
    setUserMessageCount(0);
    setSessionId(null);
    setHistory(null);
    setModuleId(null);
    setSubmoduleNumber(null);
    setTextPassage('');
    setModuleSubmoduleContextDict({});
    setFirstAssistantMessage('');
    setStartTime(null);
    setElapsedTime(0);
    setShowCompleteOverlay(false);
    setPerformanceSummary('');
    setIsGeneratingPerformanceSummary(false);
    setShowFirstMessageModal(false);
  };

  const getProgressPercent = () => {
    if (isGeneratingPerformanceSummary) return 100;
    if (showCompleteOverlay) return 100;
    if (userMessageCount <= 8) {
      return Math.round((userMessageCount / 8.4) * 100);
    }
    return 97;
  };

  return {
    // State
    chatMessages, setChatMessages,
    userMessage, setUserMessage,
    userMessageCount,
    sessionId, setSessionId,
    history,
    moduleId,
    submoduleNumber,
    textPassage,
    moduleSubmoduleContextDict,
    isSending,
    firstAssistantMessage,
    startTime, setStartTime,
    elapsedTime, setElapsedTime,
    showCompleteOverlay, setShowCompleteOverlay,
    performanceSummary,
    lastAssistantMessage,
    isGeneratingPerformanceSummary,
    showFirstMessageModal, setShowFirstMessageModal,
    hasEnoughMessages,
    messagesEndRef,
    
    // Methods
    initModuleChat,
    sendMessage,
    resetChat,
    getProgressPercent
  };
};
